/**
 * Resolves the admin API base URL.
 *
 * Order of precedence:
 *   1. runtime `public/config.json` -> `api.restUrl`
 *   2. build-time `NEXT_PUBLIC_API_BASE_URL`
 *   3. same-origin `/api/admin/v1`
 *
 * The runtime file exists because `NEXT_PUBLIC_*` is inlined by the compiler at
 * build time — an environment variable set on the container does nothing. The
 * deployment mounts config.json instead, so the API URL can change without a
 * rebuild.
 *
 * The final fallback is same-origin on purpose: nginx serves the panel and
 * proxies /api/ to Django on one hostname, so a deployment that forgets to
 * configure anything still works, and CORS never enters into it.
 */

const FALLBACK_REST_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "/api/admin/v1";

let cachedBaseUrl: string | null = null;
let inFlight: Promise<string> | null = null;

function normalize(url: string): string {
  return url.replace(/\/+$/, "");
}

export function getBaseUrlSync(): string {
  return normalize(cachedBaseUrl || FALLBACK_REST_URL);
}

export async function resolveBaseUrl(): Promise<string> {
  if (cachedBaseUrl) return cachedBaseUrl;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await fetch("/config.json", { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        const url = data?.api?.restUrl || data?.restUrl;
        if (typeof url === "string" && url.length > 0) {
          cachedBaseUrl = normalize(url);
          return cachedBaseUrl;
        }
      }
    } catch {
      // Optional in local development — fall back silently.
    }
    cachedBaseUrl = normalize(FALLBACK_REST_URL);
    return cachedBaseUrl;
  })();

  return inFlight;
}
